// Chapter pages content for /chapter/:chapter
// Must match what `src/pages/ChapterPage.js` expects.

export const CHAPTERS = [
  {
    id: 'fertility',
    name: 'Fertility',
    launchBadge: 'Coming Summer 2026',
    color: '#D4537E',
    tagline: "You shouldn't have to",
    tagline_em: 'figure this out alone',
    hero_desc:
      "You're in the medical fertility system now — and it's a lot. IVF cycles, injections, retrievals, results, losses, bills. Nova walks every step with you with real information, deep community, and unwavering support. HERO NOTE: Woman in soft morning light — hopeful, still, holding a mug. Quiet strength in a hard moment.",
    features: [
      {
        icon: '🌸',
        title: 'IVF journey tracker',
        desc: 'Map every cycle from consult to beta. Log medications, doses, and retrieval results. Nova checks in before every milestone — before you even think to ask.',
      },
      {
        icon: '🧪',
        title: 'Understanding your results',
        desc: 'AMH, FSH, AFC, estradiol — Nova explains every number in plain language, what it means for your specific journey, and exactly what to ask your doctor next.',
      },
      {
        icon: '🤍',
        title: 'Egg freezing & preservation',
        desc: 'Thinking about preserving your fertility? Nova walks you through timing, clinic selection, what to expect, and how to decide if now is right for you.',
      },
      {
        icon: '💰',
        title: 'Financial costs & aid navigator',
        desc: 'IVF costs $15K–$30K per cycle. Nova helps you find grants, employer benefits, financing options, and quality clinics at better prices.',
      },
      {
        icon: '💙',
        title: 'Emotional support & grief',
        desc: 'Failed cycles, pregnancy loss, recurrent failure — Nova doesn’t flinch from the painful parts. She meets you there with community and care.',
      },
      {
        icon: '🌿',
        title: 'Surrogacy network',
        desc: 'Intended parents and surrogates — legal guidance, agency vetting, matching stories, and a community who understands every step of this path.',
      },
    ],
    communities: [
      {
        emoji: '🌸',
        title: 'IVF Warriors — Cycle 1',
        members: 3240,
        desc: 'First IVF · daily check-ins · real talk',
        tag: 'Fertility',
      },
      {
        emoji: '🤍',
        title: 'Low AMH Warriors',
        members: 2100,
        desc: 'Navigating diminished ovarian reserve together',
        tag: 'Fertility',
      },
      {
        emoji: '💙',
        title: 'Pregnancy Loss & Hope',
        members: 1800,
        desc: 'Loss, grief, and finding the path forward',
        tag: 'Hope & grief',
      },
      {
        emoji: '🌿',
        title: 'Gestational Surrogacy Network',
        members: 1650,
        desc: 'Intended parents and surrogates — real stories',
        tag: 'Surrogacy',
      },
    ],
    doctors: [
      {
        name: 'Dr. Anika Patel, MD',
        specialty: 'Reproductive Endocrinology & Infertility',
        location: 'Columbus, OH',
      },
      {
        name: 'Dr. James Liu, MD',
        specialty: 'REI',
        location: 'Westerville, OH',
      },
      {
        name: 'Dr. Sofia Reyes, MD',
        specialty: 'REI + Surrogacy Specialist',
        location: 'Columbus, OH',
      },
    ],
    nova_intro:
      "I'm here for every part of this — the medical questions, the 2am spirals, the small wins, and the hard losses. What's on your mind?",
    suggestions: [
      'What does my AMH level mean?',
      'What should I expect at egg retrieval?',
      'How do I compare fertility clinics?',
      'How do I cope with a failed cycle?',
    ],
  },

  {
    id: 'ttc',
    name: 'Trying to Conceive',
    launchBadge: 'Coming 2026',
    color: '#7F77DD',
    tagline: "You're not",
    tagline_em: 'in this alone',
    hero_desc:
      "Tracking cycles, understanding ovulation, navigating the two-week wait — Nova helps you know when you're most fertile, what's normal, and when it might be time to see a specialist. Real support for your TTC journey.",
    features: [
      {
        icon: '📅',
        title: 'Cycle & fertile window tracking',
        desc: 'Track your cycle and know when you\'re most fertile. Nova reminds you about ovulation and helps you understand your patterns.',
      },
      {
        icon: '🧪',
        title: 'OPK & ovulation explained',
        desc: 'Learn how to read ovulation predictor kits and spot your fertile window. Nova breaks it down in plain language.',
      },
      {
        icon: '💜',
        title: 'Two-week wait support',
        desc: 'The TWW is hard. Nova checks in, shares what others found helpful, and helps you manage the anxiety of waiting.',
      },
      {
        icon: '🩺',
        title: 'When to see a doctor',
        desc: 'How long to try naturally, what to expect at a fertility workup, and questions to bring to your first appointment.',
      },
      {
        icon: '💬',
        title: 'TTC community',
        desc: 'Connect with women who get it — the hopes, the setbacks, and the small wins along the way.',
      },
    ],
    communities: [
      {
        emoji: '💜',
        title: 'TTC — First Year',
        members: 4200,
        desc: 'Natural conception · cycle tracking · support',
        tag: 'Matched to you',
      },
      {
        emoji: '📅',
        title: 'Ovulation & Timing',
        members: 3100,
        desc: 'OPKs, fertile windows, cycle tips',
        tag: 'Track smarter',
      },
    ],
    doctors: [
      {
        name: 'Dr. Lauren Brown, MD',
        specialty: 'OB-GYN · Cycle & Hormones',
        location: 'Columbus, OH',
      },
    ],
    nova_intro:
      "I'm here for your TTC journey — cycle tracking, fertile windows, and the two-week wait. What would help you today?",
    suggestions: [
      'When is my fertile window?',
      'How do I read an OPK?',
      'When should I see a doctor?',
    ],
  },

  {
    id: 'pregnancy',
    name: 'Pregnancy',
    launchBadge: 'Coming 2026',
    color: '#14B8A6',
    tagline: 'Every week is',
    tagline_em: 'something new',
    hero_desc:
      'From the first positive test to the last push — Nova knows your due date, tracks your milestones, and shows up before you even think to ask. HERO NOTE: Woman with hand on belly in warm natural light. Real and glowing — not a posed maternity shoot.',
    features: [
      {
        icon: '🗓️',
        title: 'Week-by-week guidance',
        desc: 'Nova tracks your due date and delivers what you need each week — what’s developing, what’s normal to feel, and what to discuss at your next appointment.',
      },
      {
        icon: '🩺',
        title: 'Symptom checker',
        desc: 'Is this cramping normal? Should I go in? Nova tells you what to watch, what to call about, and what needs immediate attention — clearly and calmly.',
      },
      {
        icon: '📋',
        title: 'Birth plan builder',
        desc: 'Think through your preferences — pain management, delivery options, cord banking, who\'s in the room — and get a document your care team will actually use.',
      },
      {
        icon: '📚',
        title: 'Trimester resource library',
        desc: 'Curated guides and expert articles by week. What to eat, what to avoid, how to sleep, what every prenatal test means — all in one place.',
      },
      {
        icon: '👨‍👩‍👧',
        title: 'Partner & family preparation',
        desc: 'Pregnancy affects everyone. Resources for partners, family members, and support people — so the people around you know how to actually help.',
      },
      {
        icon: '🏥',
        title: 'OB, midwife & doula finder',
        desc: 'Find your birth team matched to your preferences, insurance, and location — with reviews from women who gave birth with them.',
      },
    ],
    communities: [
      {
        emoji: '🤰',
        title: 'Due in Summer 2026',
        members: 1180,
        desc: 'Week-by-week support for summer mamas',
        tag: 'Pregnancy',
      },
      {
        emoji: '🌸',
        title: 'IVF Graduates — Pregnant',
        members: 920,
        desc: 'Extra support after fertility treatment',
        tag: 'Fertility → pregnancy',
      },
      {
        emoji: '💙',
        title: 'High-Risk Pregnancy Circle',
        members: 1400,
        desc: 'MFM, monitoring, NICU prep, community',
        tag: 'Extra care',
      },
      {
        emoji: '👶',
        title: 'First-Time Moms',
        members: 2800,
        desc: 'Every question, no judgment, real answers',
        tag: 'New moms',
      },
    ],
    doctors: [
      {
        name: 'Dr. Michelle Chen, MD',
        specialty: 'OB-GYN · High-Risk Pregnancy',
        location: 'Boston, MA',
      },
      {
        name: 'Dr. Jana Wells, CNM',
        specialty: 'Certified Nurse Midwife',
        location: 'Boston, MA',
      },
    ],
    nova_intro:
      "I'm here for every week of your pregnancy — the questions, the worries, and the moments of wonder. What would you like to know?",
    suggestions: [
      'What\'s normal in the first trimester?',
      'How do I build my birth plan?',
      'What should I know about the anatomy scan?',
      'How can my partner support me better?',
    ],
  },

  {
    id: 'postpartum',
    name: 'Motherhood',
    launchBadge: 'Coming 2026',
    color: '#9B2942',
    tagline: "Motherhood doesn't end",
    tagline_em: 'at twelve weeks',
    hero_desc:
      'From the fourth trimester through the teen years — Nova supports moms through recovery, breastfeeding, PPD, raising children at every age, and building a life you love alongside motherhood. HERO NOTE: Mom with a child — newborn, toddler, or teen. A real moment of connection. Warm, not perfect.',
    features: [
      {
        icon: '💙',
        title: 'Fourth trimester recovery',
        desc: 'Physical healing, hormonal shifts, sleep deprivation — what’s normal week by week after birth, and what needs your doctor’s attention.',
      },
      {
        icon: '🤱',
        title: 'Breastfeeding & feeding support',
        desc: 'Latch, supply, pumping, weaning, formula — Nova and a community of experienced moms are available around the clock, no judgment.',
      },
      {
        icon: '🧠',
        title: 'PPD & maternal mental health',
        desc: 'Nova checks in regularly. If something feels off she helps you name it and connects you to therapists who specialize in maternal mental health.',
      },
      {
        icon: '🗺️',
        title: 'Activities, camps & enrichment',
        desc: 'Find age-appropriate activities, summer camps, classes, and enrichment programs for your child from toddler through teen — by age and location.',
      },
      {
        icon: '📍',
        title: 'Baby & child milestone tracker',
        desc: 'From first smile to first day of high school. Nova flags what to watch at every age and when to call the pediatrician.',
      },
      {
        icon: '👨‍👩‍👧',
        title: 'Partner & family resources',
        desc: 'Postpartum affects everyone. Resources for your whole support circle — and guidance on parenting together at every stage.',
      },
    ],
    communities: [
      {
        emoji: '👶',
        title: 'Fourth Trimester Circle',
        members: 2400,
        desc: 'Recovery, breastfeeding, real talk',
        tag: 'Motherhood',
      },
      {
        emoji: '🤱',
        title: 'Breastfeeding Support',
        members: 3100,
        desc: 'Latch, supply, pumping — no judgment',
        tag: 'Feeding support',
      },
      {
        emoji: '🧠',
        title: 'PPD Recovery Circle',
        members: 1600,
        desc: 'Postpartum mental health, safe space',
        tag: 'PPD support',
      },
      {
        emoji: '🌟',
        title: 'Moms of Tweens & Teens',
        members: 2800,
        desc: 'Navigating the older kid years together',
        tag: 'Older kids',
      },
    ],
    doctors: [
      {
        name: 'Dr. Laura Banks, MD',
        specialty: 'OB-GYN · Postpartum Care',
        location: 'Newton, MA',
      },
      {
        name: 'Dr. Diana Park, PhD',
        specialty: 'Maternal Mental Health',
        location: 'Boston, MA',
      },
    ],
    nova_intro:
      "From newborn questions to navigating your teenager — I'm here across every stage of motherhood. What do you need right now?",
    suggestions: [
      'How do I know if I have PPD?',
      'Find summer camps near me for my 8-year-old',
      'When will I feel like myself again?',
      'My teenager is struggling — how do I help?',
    ],
  },
];

